import React, { useState, useEffect, useCallback } from 'react';
import {
  collection, query, where, orderBy, limit,
  getDocs, doc, getDoc, startAfter, Timestamp,
} from 'firebase/firestore';
import { db } from '../../../../firebase';
import { useAuth } from '../../../auth/components/AuthProvider';

const PAGE_SIZE = 25;

/* ─── helpers ──────────────────────────────────────────────────────────── */
function fmtDate(val) {
  if (!val) return '—';
  const d = val?.toDate?.() ?? (val instanceof Date ? val : null);
  if (!d) return '—';
  return d.toLocaleDateString('es-CL', { day: '2-digit', month: '2-digit', year: 'numeric' })
    + ' ' + d.toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' });
}
function fmtDateShort(val) {
  if (!val) return '—';
  const d = val?.toDate?.() ?? (val instanceof Date ? val : null);
  return d ? d.toLocaleDateString('es-CL', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—';
}
function fmtMoney(n) {
  if (n == null) return '—';
  return '$' + Number(n).toLocaleString('es-CL');
}
function fmtDuracion(inicio, fin) {
  if (!inicio || !fin) return '—';
  const a = inicio?.toDate?.() ?? new Date(inicio);
  const b = fin?.toDate?.() ?? new Date(fin);
  const dias = Math.round((b - a) / 86400000);
  if (dias <= 0) return '—';
  if (dias >= 360) return `${Math.round(dias / 30)} meses`;
  if (dias >= 28)  return `${Math.round(dias / 30)} mes`;
  return `${dias} días`;
}
function diasRestantes(fin) {
  if (!fin) return null;
  const f = fin?.toDate?.() ?? new Date(fin);
  return Math.ceil((f - new Date()) / 86400000);
}

function isPlanEmpresa(p) { return (p || '').startsWith('empresa_'); }
function isPlanAddon(p)   { return (p || '').startsWith('addon_'); }
function isPlanCliente(p) { return !!p && !isPlanEmpresa(p) && !isPlanAddon(p); }
function esWebpay(p)      { return (p?.provider || p?.metodoPago || '') === 'transbank' || (p?.provider || '') === 'transbank'; }
function esTransferencia(p) { return (p?.provider || p?.metodoPago || '').includes('transferencia'); }

function membresiaEstadoCalc(m) {
  const fin = m?.fechaFin?.toDate?.() ?? (m?.fechaFin ? new Date(m.fechaFin) : null);
  if (!fin) return 'desconocido';
  return fin > new Date() ? (m?.estado || 'activa') : 'vencida';
}

/* ─── micro-components ─────────────────────────────────────────────────── */
function TipoBadge({ planId }) {
  if (!planId) return <span className="text-gray-400 text-xs">—</span>;
  if (isPlanEmpresa(planId)) return <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-violet-100 text-violet-700">Empresa</span>;
  if (isPlanAddon(planId))   return <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-sky-100 text-sky-700">Addon</span>;
  return <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-teal-100 text-teal-700">Cliente</span>;
}
function PagoBadge({ status }) {
  const cfg = {
    paid:      { cls: 'bg-green-100 text-green-800',  label: 'Pagado'    },
    pending:   { cls: 'bg-amber-100 text-amber-800',  label: 'Pendiente' },
    failed:    { cls: 'bg-red-100 text-red-800',      label: 'Fallido'   },
    canceled:  { cls: 'bg-gray-100 text-gray-600',    label: 'Cancelado' },
    cancelled: { cls: 'bg-gray-100 text-gray-600',    label: 'Cancelado' },
  };
  const { cls, label } = cfg[status] ?? { cls: 'bg-gray-100 text-gray-500', label: status || '—' };
  return <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${cls}`}>{label}</span>;
}
function MetodoBadge({ p }) {
  if (esTransferencia(p)) return <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-blue-100 text-blue-700">Transferencia</span>;
  if (esWebpay(p))        return <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-indigo-100 text-indigo-700">Webpay</span>;
  return <span className="text-xs text-gray-400">{p?.provider || '—'}</span>;
}
function MembresiaBadge({ estado }) {
  if (estado === 'activa')  return <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-800">Activa</span>;
  if (estado === 'vencida') return <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-600">Vencida</span>;
  return <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700">{estado}</span>;
}
function SummaryCard({ label, value, sub, colorCls, loading }) {
  return (
    <div className={`rounded-xl border p-4 ${colorCls}`}>
      <div className="text-xl font-black">{loading ? <span className="animate-pulse">...</span> : value}</div>
      <div className="text-xs font-semibold mt-0.5 opacity-80">{label}</div>
      {sub && <div className="text-[11px] opacity-60 mt-0.5">{sub}</div>}
    </div>
  );
}
function DL({ label, value, mono = false, wide = false }) {
  return (
    <>
      <dt className="text-gray-400 text-xs self-center">{label}</dt>
      <dd className={`text-sm ${mono ? 'font-mono text-xs break-all' : 'text-gray-900'} ${wide ? 'col-span-3' : ''}`}>{value ?? '—'}</dd>
    </>
  );
}
function Section({ title, children, className = '' }) {
  return (
    <div className={`rounded-xl border border-gray-200 overflow-hidden ${className}`}>
      <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
        <p className="text-xs font-black uppercase tracking-wider text-gray-500">{title}</p>
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

/* ─── main component ───────────────────────────────────────────────────── */
export default function PagosMembresiasAdmin() {
  const { user, rol } = useAuth();
  const [allowed, setAllowed]   = useState(null);
  const [activeTab, setActiveTab] = useState('pagos');

  // summary
  const [summary, setSummary]           = useState({ totalRecaudado: 0, webpayTotal: 0, transferenciaTotal: 0, paidCount: 0, pendingCount: 0, failedCount: 0, membresiasActivas: 0, porVencer: 0 });
  const [summaryLoading, setSummaryLoading] = useState(true);

  // pagos tab
  const [payments, setPayments]   = useState([]);
  const [lastDoc, setLastDoc]     = useState(null);
  const [hasMore, setHasMore]     = useState(true);
  const [tableLoading, setTableLoading] = useState(false);
  const [filters, setFilters]     = useState({ metodoPago: '', status: '', planTipo: '', dateFrom: '', dateTo: '', search: '' });

  // membresias tab
  const [membresias, setMembresias]   = useState([]);
  const [memLastDoc, setMemLastDoc]   = useState(null);
  const [memHasMore, setMemHasMore]   = useState(true);
  const [memLoading, setMemLoading]   = useState(false);
  const [memFilters, setMemFilters]   = useState({ estadoFiltro: '', planTipo: '' });

  // gratuitos tab
  const [gratuitos, setGratuitos]           = useState([]);
  const [gratuitosLoading, setGratuitosLoading] = useState(false);
  const [gratuitosFiltro, setGratuitosFiltro]   = useState('');

  // detail modal
  const [detailPayment, setDetailPayment]       = useState(null);
  const [detailMembresia, setDetailMembresia]   = useState(null);
  const [detailUser, setDetailUser]             = useState(null);
  const [userPayments, setUserPayments]         = useState([]);
  const [detailUserOpen, setDetailUserOpen]     = useState(false);
  const [detailTechOpen, setDetailTechOpen]     = useState(false);

  const isAdmin = rol === 'admin';

  useEffect(() => {
    if (user && rol !== undefined) setAllowed(isAdmin);
  }, [user, rol, isAdmin]);

  /* ── summary ─────────────────────────────────────────────────────── */
  const loadSummary = useCallback(async () => {
    if (!isAdmin) return;
    setSummaryLoading(true);
    try {
      const now   = new Date();
      const ts30  = Timestamp.fromDate(new Date(now.getTime() - 30 * 86400000));
      const ts7   = new Date(now.getTime() + 7 * 86400000);

      const [snapWp, snapTr] = await Promise.all([
        getDocs(query(collection(db, 'payments'), where('provider', '==', 'transbank'),              where('createdAt', '>=', ts30), orderBy('createdAt', 'desc'), limit(2000))),
        getDocs(query(collection(db, 'payments'), where('provider', '==', 'transferencia_bancaria'), where('createdAt', '>=', ts30), orderBy('createdAt', 'desc'), limit(2000))),
      ]);

      let totalRecaudado = 0, webpayTotal = 0, transferenciaTotal = 0, paidCount = 0, pendingCount = 0, failedCount = 0;
      const process = (snap, isT) => {
        snap.docs.forEach(d => {
          const data = d.data();
          const st = (data.status || '').toLowerCase();
          if (st === 'paid') {
            const m = Number(data.amount) || 0;
            paidCount++; totalRecaudado += m;
            if (isT) transferenciaTotal += m; else webpayTotal += m;
          } else if (st === 'pending')            pendingCount++;
          else if (st === 'failed')               failedCount++;
        });
      };
      process(snapWp, false);
      process(snapTr, true);

      // activas desde colección membresias
      let activas = 0, porVencer = 0;
      try {
        const memSnap = await getDocs(query(collection(db, 'membresias'), where('estado', '==', 'activa'), orderBy('fechaFin', 'desc'), limit(2000)));
        memSnap.docs.forEach(d => {
          const fin = d.data().fechaFin?.toDate?.() ?? null;
          if (fin && fin > now) { activas++; if (fin <= ts7) porVencer++; }
        });
      } catch (_) { /* índice no disponible, skip */ }

      setSummary({ totalRecaudado, webpayTotal, transferenciaTotal, paidCount, pendingCount, failedCount, membresiasActivas: activas, porVencer });
    } catch (err) { console.error('summary:', err); }
    finally { setSummaryLoading(false); }
  }, [isAdmin]);

  useEffect(() => { loadSummary(); }, [loadSummary]);

  /* ── pagos ───────────────────────────────────────────────────────── */
  const loadPayments = useCallback(async (isReset = false) => {
    if (!isAdmin) return;
    setTableLoading(true);
    try {
      const ref = collection(db, 'payments');
      const constraints = [orderBy('createdAt', 'desc'), limit(PAGE_SIZE * 2)];
      if (filters.metodoPago === 'webpay')        constraints.unshift(where('provider', '==', 'transbank'));
      else if (filters.metodoPago === 'transferencia') constraints.unshift(where('provider', '==', 'transferencia_bancaria'));
      if (!isReset && lastDoc) constraints.push(startAfter(lastDoc));

      const snap = await getDocs(query(ref, ...constraints));
      let list = snap.docs.map(d => ({ id: d.id, ...d.data() }));

      // client-side filters
      if (filters.status)   list = list.filter(p => p.status === filters.status);
      if (filters.planTipo === 'cliente') list = list.filter(p => isPlanCliente(p.planId));
      if (filters.planTipo === 'empresa') list = list.filter(p => isPlanEmpresa(p.planId));
      if (filters.planTipo === 'addon')   list = list.filter(p => isPlanAddon(p.planId));
      if (filters.dateFrom) { const from = new Date(filters.dateFrom); list = list.filter(p => { const d = p.createdAt?.toDate?.(); return d && d >= from; }); }
      if (filters.dateTo)   { const to = new Date(filters.dateTo); to.setHours(23,59,59,999); list = list.filter(p => { const d = p.createdAt?.toDate?.(); return d && d <= to; }); }
      if (filters.search) {
        const s = filters.search.trim().toLowerCase();
        list = list.filter(p =>
          (p.uid || '').toLowerCase().includes(s) ||
          (p.userEmail || '').toLowerCase().includes(s) ||
          (p.planId || '').toLowerCase().includes(s) ||
          (p.planName || '').toLowerCase().includes(s) ||
          (p.buyOrder || '').toLowerCase().includes(s)
        );
      }
      list = list.slice(0, PAGE_SIZE);

      const nextLast = snap.docs.length ? snap.docs[snap.docs.length - 1] : null;
      if (isReset) { setPayments(list); setLastDoc(nextLast); }
      else          { setPayments(prev => [...prev, ...list]); setLastDoc(nextLast); }
      setHasMore(snap.docs.length >= PAGE_SIZE);
    } catch (err) { console.error('pagos:', err); setPayments([]); }
    finally { setTableLoading(false); }
  }, [isAdmin, filters, lastDoc]);

  useEffect(() => {
    setLastDoc(null);
    loadPayments(true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.metodoPago, filters.status, filters.planTipo, filters.dateFrom, filters.dateTo]);

  /* ── membresias ──────────────────────────────────────────────────── */
  const loadMembresias = useCallback(async (isReset = false) => {
    if (!isAdmin) return;
    setMemLoading(true);
    try {
      const ref = collection(db, 'membresias');
      const constraints = [orderBy('creadoEn', 'desc'), limit(PAGE_SIZE * 2)];
      if (!isReset && memLastDoc) constraints.push(startAfter(memLastDoc));
      const snap = await getDocs(query(ref, ...constraints));
      let list = snap.docs.map(d => ({ id: d.id, ...d.data() }));

      const now = new Date();
      if (memFilters.estadoFiltro === 'activa')  list = list.filter(m => { const f = m.fechaFin?.toDate?.(); return f && f > now; });
      if (memFilters.estadoFiltro === 'vencida') list = list.filter(m => { const f = m.fechaFin?.toDate?.(); return !f || f <= now; });
      if (memFilters.planTipo === 'cliente') list = list.filter(m => isPlanCliente(m.tipo));
      if (memFilters.planTipo === 'empresa') list = list.filter(m => isPlanEmpresa(m.tipo));
      list = list.slice(0, PAGE_SIZE);

      const nextLast = snap.docs.length ? snap.docs[snap.docs.length - 1] : null;
      if (isReset) { setMembresias(list); setMemLastDoc(nextLast); }
      else          { setMembresias(prev => [...prev, ...list]); setMemLastDoc(nextLast); }
      setMemHasMore(snap.docs.length >= PAGE_SIZE);
    } catch (err) { console.error('membresías:', err); setMembresias([]); }
    finally { setMemLoading(false); }
  }, [isAdmin, memFilters, memLastDoc]);

  useEffect(() => {
    if (activeTab === 'membresias') { setMemLastDoc(null); loadMembresias(true); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, memFilters.estadoFiltro, memFilters.planTipo]);

  /* ── gratuitos ───────────────────────────────────────────────────── */
  const loadGratuitos = useCallback(async () => {
    if (!isAdmin) return;
    setGratuitosLoading(true);
    try {
      const snap = await getDocs(query(collection(db, 'usuarios'), where('rol', '==', 'cliente'), limit(500)));
      const now = new Date();
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(u => {
        const m = u.membresia;
        if (!m) return true;
        const fin = m.fechaFin?.toDate?.() ?? (m.fechaFin ? new Date(m.fechaFin) : null);
        // sin membresía o vencida o plan gratis/visitor
        return !fin || fin <= now || !['basic','premium','premium_vehicular','premium_conductor','dashboard_base'].includes((m.tipo || '').toLowerCase());
      });
      setGratuitos(list);
    } catch (err) { console.error('gratuitos:', err); setGratuitos([]); }
    finally { setGratuitosLoading(false); }
  }, [isAdmin]);

  useEffect(() => {
    if (activeTab === 'gratuitos') loadGratuitos();
  }, [activeTab, loadGratuitos]);

  /* ── detail modal ────────────────────────────────────────────────── */
  const openDetail = async (p) => {
    setDetailPayment(p);
    setDetailMembresia(null);
    setDetailUser(null);
    setUserPayments([]);
    setDetailUserOpen(false);
    setDetailTechOpen(false);
    if (p.id && !isPlanAddon(p.planId)) {
      try {
        const snap = await getDocs(query(collection(db, 'membresias'), where('paymentId', '==', p.id), limit(1)));
        if (!snap.empty) setDetailMembresia({ id: snap.docs[0].id, ...snap.docs[0].data() });
      } catch (_) { /* sin índice */ }
    }
  };

  const loadUserDetail = async () => {
    if (!detailPayment?.uid || detailUserOpen) return;
    setDetailUserOpen(true);
    try {
      const uSnap = await getDoc(doc(db, 'usuarios', detailPayment.uid));
      setDetailUser(uSnap.exists() ? { id: uSnap.id, ...uSnap.data() } : null);
      const pSnap = await getDocs(query(collection(db, 'payments'), where('uid', '==', detailPayment.uid), orderBy('createdAt', 'desc'), limit(10)));
      setUserPayments(pSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (e) { console.error(e); }
  };

  /* ── guards ─────────────────────────────────────────────────────── */
  if (allowed === false) return <div className="p-6 bg-white rounded-lg shadow"><p className="text-red-600 font-medium">Acceso denegado. Solo administradores.</p></div>;
  if (allowed === null)  return <div className="p-6 flex justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" /></div>;

  /* ── render ─────────────────────────────────────────────────────── */
  const L = summaryLoading;
  const hasFact = detailPayment?.documentoTipo === 'factura' && detailPayment?.datosFactura;
  const hasTrans = esTransferencia(detailPayment);
  const descPct = detailPayment?.descuentoPorcentaje ?? (detailPayment?.descuento ? 20 : 0);

  return (
    <div className="space-y-5">

      {/* header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-black text-gray-900">💳 Pagos y Membresías</h1>
        <button onClick={loadSummary} className="text-xs text-indigo-600 hover:text-indigo-800 font-semibold border border-indigo-200 rounded-lg px-3 py-1.5 hover:bg-indigo-50 transition-colors">
          Actualizar resumen
        </button>
      </div>

      {/* summary */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <SummaryCard label="Recaudado (30 d)" value={fmtMoney(summary.totalRecaudado)} colorCls="bg-green-50 border-green-200 text-green-800" loading={L} />
        <SummaryCard label="Webpay" value={fmtMoney(summary.webpayTotal)} sub={`${summary.paidCount} pagados total`} colorCls="bg-indigo-50 border-indigo-200 text-indigo-800" loading={L} />
        <SummaryCard label="Transferencia" value={fmtMoney(summary.transferenciaTotal)} colorCls="bg-blue-50 border-blue-200 text-blue-800" loading={L} />
        <SummaryCard label="Membresías activas" value={summary.membresiasActivas} sub={`${summary.porVencer} vencen en 7 días`} colorCls="bg-teal-50 border-teal-200 text-teal-800" loading={L} />
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <SummaryCard label="Pagados (30 d)" value={summary.paidCount} colorCls="bg-emerald-50 border-emerald-200 text-emerald-800" loading={L} />
        <SummaryCard label="Pendientes (30 d)" value={summary.pendingCount} colorCls="bg-amber-50 border-amber-200 text-amber-800" loading={L} />
        <SummaryCard label="Fallidos (30 d)" value={summary.failedCount} colorCls="bg-red-50 border-red-200 text-red-800" loading={L} />
        <SummaryCard label="Por vencer (7 d)" value={summary.porVencer} colorCls="bg-orange-50 border-orange-200 text-orange-800" loading={L} />
      </div>

      {/* tabs */}
      <div className="flex gap-1 border-b border-gray-200">
        {[['pagos', 'Pagos'], ['membresias', 'Membresías'], ['gratuitos', `Gratuito${gratuitos.length ? ` (${gratuitos.length})` : ''}`]].map(([key, label]) => (
          <button key={key} onClick={() => setActiveTab(key)}
            className={`px-5 py-2.5 text-sm font-bold border-b-2 transition-colors ${activeTab === key ? 'border-indigo-600 text-indigo-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
            {label}
          </button>
        ))}
      </div>

      {/* ── TAB: PAGOS ───────────────────────────────────────────────── */}
      {activeTab === 'pagos' && (
        <>
          {/* filters */}
          <div className="bg-white rounded-xl border border-gray-200 p-4 flex flex-wrap items-end gap-3">
            {[
              { label: 'Método de pago', key: 'metodoPago', opts: [['', 'Todos'], ['webpay', 'Webpay'], ['transferencia', 'Transferencia']] },
              { label: 'Estado', key: 'status', opts: [['', 'Todos'], ['paid', 'Pagado'], ['pending', 'Pendiente'], ['failed', 'Fallido'], ['canceled', 'Cancelado']] },
              { label: 'Tipo usuario', key: 'planTipo', opts: [['', 'Todos'], ['cliente', 'Cliente'], ['empresa', 'Empresa'], ['addon', 'Addon']] },
            ].map(({ label, key, opts }) => (
              <div key={key}>
                <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">{label}</label>
                <select value={filters[key]} onChange={e => setFilters(f => ({ ...f, [key]: e.target.value }))}
                  className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm">
                  {opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
            ))}
            <div>
              <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">Desde</label>
              <input type="date" value={filters.dateFrom} onChange={e => setFilters(f => ({ ...f, dateFrom: e.target.value }))}
                className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm" />
            </div>
            <div>
              <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">Hasta</label>
              <input type="date" value={filters.dateTo} onChange={e => setFilters(f => ({ ...f, dateTo: e.target.value }))}
                className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm" />
            </div>
            <div className="flex-1 min-w-[220px]">
              <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">Buscar uid / email / plan / buyOrder</label>
              <div className="flex gap-2">
                <input type="text" placeholder="Buscar..." value={filters.search}
                  onChange={e => setFilters(f => ({ ...f, search: e.target.value }))}
                  onKeyDown={e => e.key === 'Enter' && loadPayments(true)}
                  className="flex-1 border border-gray-300 rounded-lg px-3 py-1.5 text-sm" />
                <button onClick={() => loadPayments(true)}
                  className="bg-indigo-600 text-white px-4 py-1.5 rounded-lg text-sm font-bold hover:bg-indigo-700 transition-colors">
                  Buscar
                </button>
              </div>
            </div>
          </div>

          {/* table */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    {['Fecha pago', 'Usuario', 'Tipo', 'Plan', 'Meses', 'Monto', 'Método', 'Documento', 'Estado', ''].map(h => (
                      <th key={h} className="px-3 py-2.5 text-left text-[10px] font-black uppercase tracking-wider text-gray-500 whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {tableLoading && payments.length === 0 ? (
                    <tr><td colSpan={10} className="px-4 py-10 text-center text-gray-400">Cargando...</td></tr>
                  ) : payments.length === 0 ? (
                    <tr><td colSpan={10} className="px-4 py-10 text-center text-gray-400">Sin resultados</td></tr>
                  ) : payments.map(p => (
                    <tr key={p.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-3 py-2 whitespace-nowrap text-gray-600 text-xs">{fmtDate(p.createdAt)}</td>
                      <td className="px-3 py-2 min-w-[150px]">
                        <div className="font-mono text-[10px] text-gray-500 truncate max-w-[140px]" title={p.uid}>{p.uid}</div>
                        {p.userEmail && <div className="text-xs text-gray-700 truncate max-w-[140px]">{p.userEmail}</div>}
                      </td>
                      <td className="px-3 py-2"><TipoBadge planId={p.planId} /></td>
                      <td className="px-3 py-2 max-w-[160px]">
                        <div className="text-xs text-gray-800 font-medium">{p.planName || p.planId || '—'}</div>
                        {p.planId && p.planName && p.planId !== p.planName && <div className="text-[10px] text-gray-400 font-mono">{p.planId}</div>}
                      </td>
                      <td className="px-3 py-2 text-center text-xs text-gray-700">{p.meses ?? '—'}</td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <div className="font-bold text-gray-900">{fmtMoney(p.amount)}</div>
                        {p.descuento && p.amountOriginal && p.amountOriginal !== p.amount && (
                          <div className="text-[10px] text-gray-400 line-through">{fmtMoney(p.amountOriginal)}</div>
                        )}
                        {p.descuento && <span className="text-[9px] font-black text-amber-600 bg-amber-100 px-1 rounded">-{descPct}%</span>}
                      </td>
                      <td className="px-3 py-2"><MetodoBadge p={p} /></td>
                      <td className="px-3 py-2">
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${p.documentoTipo === 'factura' ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-600'}`}>
                          {p.documentoTipo === 'factura' ? 'Factura' : 'Boleta'}
                        </span>
                      </td>
                      <td className="px-3 py-2"><PagoBadge status={p.status} /></td>
                      <td className="px-3 py-2">
                        <button onClick={() => openDetail(p)} className="text-indigo-600 hover:text-indigo-800 text-xs font-bold">Ver</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {hasMore && payments.length > 0 && (
              <div className="px-4 py-3 border-t border-gray-100 text-center">
                <button onClick={() => loadPayments(false)} disabled={tableLoading}
                  className="text-indigo-600 hover:text-indigo-800 font-semibold text-sm disabled:opacity-50">
                  {tableLoading ? 'Cargando...' : 'Cargar más'}
                </button>
              </div>
            )}
          </div>
        </>
      )}

      {/* ── TAB: MEMBRESÍAS ──────────────────────────────────────────── */}
      {activeTab === 'membresias' && (
        <>
          {/* filters */}
          <div className="bg-white rounded-xl border border-gray-200 p-4 flex flex-wrap items-end gap-3">
            {[
              { label: 'Estado', key: 'estadoFiltro', opts: [['', 'Todas'], ['activa', 'Activas'], ['vencida', 'Vencidas']] },
              { label: 'Tipo', key: 'planTipo', opts: [['', 'Todos'], ['cliente', 'Cliente'], ['empresa', 'Empresa']] },
            ].map(({ label, key, opts }) => (
              <div key={key}>
                <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">{label}</label>
                <select value={memFilters[key]} onChange={e => setMemFilters(f => ({ ...f, [key]: e.target.value }))}
                  className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm">
                  {opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
            ))}
            <button onClick={() => { setMemLastDoc(null); loadMembresias(true); }}
              className="ml-auto border border-gray-200 rounded-lg px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-50 transition-colors font-medium">
              Recargar
            </button>
          </div>

          {/* table */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    {['Usuario', 'Plan', 'Tipo', 'Estado', 'Fecha inicio', 'Vencimiento', 'Duración', 'Días restantes', 'Método pago', 'Ref. pago'].map(h => (
                      <th key={h} className="px-3 py-2.5 text-left text-[10px] font-black uppercase tracking-wider text-gray-500 whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {memLoading && membresias.length === 0 ? (
                    <tr><td colSpan={10} className="px-4 py-10 text-center text-gray-400">Cargando...</td></tr>
                  ) : membresias.length === 0 ? (
                    <tr><td colSpan={10} className="px-4 py-10 text-center text-gray-400">Sin resultados</td></tr>
                  ) : membresias.map(m => {
                    const estado = membresiaEstadoCalc(m);
                    const dias   = diasRestantes(m.fechaFin);
                    const esActiva = estado === 'activa';
                    return (
                      <tr key={m.id} className={`hover:bg-gray-50 transition-colors ${!esActiva ? 'opacity-60' : ''}`}>
                        <td className="px-3 py-2">
                          <div className="font-mono text-[10px] text-gray-500 truncate max-w-[130px]" title={m.userId}>{m.userId}</div>
                        </td>
                        <td className="px-3 py-2 max-w-[160px]">
                          <div className="text-xs font-medium text-gray-800">{m.planName || m.tipo || '—'}</div>
                          {m.tipo && m.planName && m.tipo !== m.planName && <div className="text-[10px] text-gray-400 font-mono">{m.tipo}</div>}
                        </td>
                        <td className="px-3 py-2"><TipoBadge planId={m.tipo} /></td>
                        <td className="px-3 py-2"><MembresiaBadge estado={estado} /></td>
                        <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-700">{fmtDateShort(m.fechaInicio)}</td>
                        <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-700">{fmtDateShort(m.fechaFin)}</td>
                        <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-700">{fmtDuracion(m.fechaInicio, m.fechaFin)}</td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          {esActiva && dias != null ? (
                            <span className={`text-xs font-bold ${dias <= 7 ? 'text-orange-600' : dias <= 30 ? 'text-amber-600' : 'text-green-700'}`}>
                              {dias} días
                            </span>
                          ) : (
                            <span className="text-xs text-gray-400">—</span>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          {m.metodoPago?.includes('transferencia')
                            ? <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-blue-100 text-blue-700">Transferencia</span>
                            : <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-indigo-100 text-indigo-700">Webpay</span>}
                        </td>
                        <td className="px-3 py-2 text-xs text-gray-400 font-mono">{m.paymentId ? m.paymentId.slice(0, 8) + '…' : '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {memHasMore && membresias.length > 0 && (
              <div className="px-4 py-3 border-t border-gray-100 text-center">
                <button onClick={() => loadMembresias(false)} disabled={memLoading}
                  className="text-indigo-600 hover:text-indigo-800 font-semibold text-sm disabled:opacity-50">
                  {memLoading ? 'Cargando...' : 'Cargar más'}
                </button>
              </div>
            )}
          </div>
        </>
      )}

      {/* ── TAB: GRATUITO ────────────────────────────────────────────── */}
      {activeTab === 'gratuitos' && (
        <>
          <div className="bg-white rounded-xl border border-gray-200 p-4 flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[220px]">
              <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">Buscar nombre / email / uid</label>
              <input type="text" placeholder="Buscar..." value={gratuitosFiltro}
                onChange={e => setGratuitosFiltro(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm" />
            </div>
            <button onClick={loadGratuitos}
              className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-50 transition-colors font-medium">
              Recargar
            </button>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="px-4 py-2.5 border-b border-gray-100 bg-gray-50 flex items-center justify-between">
              <p className="text-xs font-black uppercase tracking-wider text-gray-500">Clientes sin plan de pago activo</p>
              <span className="text-xs text-gray-400">{gratuitos.length} usuarios</span>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    {['Usuario', 'Email', 'Nombre', 'Fecha registro', 'Último plan', 'Venció', 'Estado'].map(h => (
                      <th key={h} className="px-3 py-2.5 text-left text-[10px] font-black uppercase tracking-wider text-gray-500 whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {gratuitosLoading ? (
                    <tr><td colSpan={7} className="px-4 py-10 text-center text-gray-400">Cargando...</td></tr>
                  ) : (() => {
                    const s = gratuitosFiltro.trim().toLowerCase();
                    const filtered = s
                      ? gratuitos.filter(u =>
                          (u.id || '').toLowerCase().includes(s) ||
                          (u.email || '').toLowerCase().includes(s) ||
                          (u.nombre || u.displayName || '').toLowerCase().includes(s)
                        )
                      : gratuitos;
                    if (!filtered.length) return <tr><td colSpan={7} className="px-4 py-10 text-center text-gray-400">Sin resultados</td></tr>;
                    return filtered.map(u => {
                      const m = u.membresia;
                      const fin = m?.fechaFin?.toDate?.() ?? (m?.fechaFin ? new Date(m.fechaFin) : null);
                      const vencio = fin ? fin <= new Date() : null;
                      const nombre = u.nombre || u.displayName || u.nombreCompleto || '—';
                      const creadoEn = u.creadoEn || u.createdAt || u.fechaCreacion;
                      return (
                        <tr key={u.id} className="hover:bg-gray-50 transition-colors">
                          <td className="px-3 py-2">
                            <span className="font-mono text-[10px] text-gray-500 truncate max-w-[120px] block" title={u.id}>{u.id}</span>
                          </td>
                          <td className="px-3 py-2 text-xs text-gray-700 max-w-[180px]">
                            <span className="truncate block" title={u.email}>{u.email || '—'}</span>
                          </td>
                          <td className="px-3 py-2 text-xs text-gray-800">{nombre}</td>
                          <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-500">{fmtDateShort(creadoEn)}</td>
                          <td className="px-3 py-2 text-xs text-gray-500">{m?.tipo || '—'}</td>
                          <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-500">{fin ? fmtDateShort(m.fechaFin) : '—'}</td>
                          <td className="px-3 py-2">
                            {!m || !fin
                              ? <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-slate-100 text-slate-600">Sin plan</span>
                              : vencio
                                ? <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-600">Vencido</span>
                                : <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-700">Activo gratis</span>
                            }
                          </td>
                        </tr>
                      );
                    });
                  })()}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* ── MODAL DETALLE ────────────────────────────────────────────── */}
      {detailPayment && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-start justify-center min-h-screen px-4 py-8">
            <div className="fixed inset-0 bg-black/60" onClick={() => setDetailPayment(null)} />
            <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl flex flex-col overflow-hidden">

              {/* modal header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                <div className="flex items-center gap-3">
                  <h2 className="text-lg font-black text-gray-900">Detalle del pago</h2>
                  <PagoBadge status={detailPayment.status} />
                  <MetodoBadge p={detailPayment} />
                </div>
                <button onClick={() => setDetailPayment(null)} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">×</button>
              </div>

              <div className="p-6 overflow-y-auto max-h-[78vh] space-y-4">

                {/* pago principal */}
                <Section title="Pago">
                  <dl className="grid grid-cols-[auto_1fr_auto_1fr] gap-x-4 gap-y-2">
                    <DL label="ID pago"       value={<span className="font-mono text-xs">{detailPayment.id}</span>} />
                    <DL label="Fecha"         value={fmtDate(detailPayment.createdAt)} />
                    <DL label="Plan"          value={detailPayment.planName || detailPayment.planId} />
                    <DL label="Plan ID"       value={<span className="font-mono text-xs">{detailPayment.planId}</span>} />
                    <DL label="Meses"         value={detailPayment.meses ?? '—'} />
                    <DL label="Días plan"     value={detailPayment.planDays ?? '—'} />
                    <DL label="Monto final"   value={<span className="font-bold text-green-700">{fmtMoney(detailPayment.amount)}</span>} />
                    <DL label="Monto original" value={fmtMoney(detailPayment.amountOriginal)} />
                    {detailPayment.descuento && <><dt className="text-gray-400 text-xs">Descuento</dt><dd className="text-amber-600 text-xs font-bold col-span-3">Sí — {detailPayment.descuentoPorcentaje ?? 20}% aplicado</dd></>}
                    <DL label="Documento"     value={detailPayment.documentoTipo === 'factura' ? 'Factura electrónica' : 'Boleta electrónica'} />
                    <DL label="Moneda"        value={detailPayment.currency || 'CLP'} />
                  </dl>
                </Section>

                {/* usuario */}
                <Section title="Usuario">
                  <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 mb-3">
                    <DL label="UID"   value={<span className="font-mono text-xs">{detailPayment.uid}</span>} />
                    <DL label="Email" value={detailPayment.userEmail || '—'} />
                  </dl>
                  {!detailUserOpen ? (
                    <button onClick={loadUserDetail} className="text-indigo-600 hover:text-indigo-800 text-xs font-bold border border-indigo-200 rounded-lg px-3 py-1.5 hover:bg-indigo-50 transition-colors">
                      Cargar historial del usuario
                    </button>
                  ) : (
                    <div className="space-y-3">
                      {detailUser ? (
                        <>
                          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-sm bg-gray-50 rounded-xl p-3">
                            <DL label="Email"  value={detailUser.email} />
                            <DL label="Nombre" value={detailUser.nombre || detailUser.displayName} />
                            <DL label="Rol"    value={detailUser.rol || '—'} />
                            <DL label="Plan actual" value={detailUser.plan || detailUser.membresia?.tipo || '—'} />
                            <DL label="Membresía hasta" value={fmtDateShort(detailUser.membresia?.fechaFin)} />
                            <DL label="Estado membresía" value={detailUser.membresia?.estado || '—'} />
                          </dl>
                          {userPayments.length > 0 && (
                            <div>
                              <p className="text-xs font-black uppercase text-gray-400 mb-2">Últimos {userPayments.length} pagos del usuario</p>
                              <div className="space-y-1">
                                {userPayments.map(up => (
                                  <div key={up.id} className="flex items-center justify-between text-xs bg-gray-50 rounded-lg px-3 py-1.5 gap-2">
                                    <span className="text-gray-500">{fmtDateShort(up.createdAt)}</span>
                                    <span className="font-medium truncate flex-1">{up.planName || up.planId}</span>
                                    <span className="font-bold">{fmtMoney(up.amount)}</span>
                                    <PagoBadge status={up.status} />
                                    <MetodoBadge p={up} />
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </>
                      ) : (
                        <p className="text-sm text-gray-400">Usuario no encontrado o sin datos.</p>
                      )}
                    </div>
                  )}
                </Section>

                {/* membresía asociada */}
                {detailMembresia ? (
                  <Section title="Membresía activada">
                    <dl className="grid grid-cols-[auto_1fr_auto_1fr] gap-x-4 gap-y-2">
                      <DL label="ID membresía"  value={<span className="font-mono text-xs">{detailMembresia.id}</span>} />
                      <DL label="Estado"        value={<MembresiaBadge estado={membresiaEstadoCalc(detailMembresia)} />} />
                      <DL label="Fecha inicio"  value={fmtDate(detailMembresia.fechaInicio)} />
                      <DL label="Vencimiento"   value={fmtDate(detailMembresia.fechaFin)} />
                      <DL label="Duración"      value={fmtDuracion(detailMembresia.fechaInicio, detailMembresia.fechaFin)} />
                      <DL label="Días registrados" value={detailMembresia.dias ?? '—'} />
                      {membresiaEstadoCalc(detailMembresia) === 'activa' && (
                        <><dt className="text-gray-400 text-xs">Días restantes</dt>
                          <dd className="text-green-700 font-bold text-sm col-span-3">{diasRestantes(detailMembresia.fechaFin)} días</dd></>
                      )}
                    </dl>
                  </Section>
                ) : !isPlanAddon(detailPayment.planId) && (
                  <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700">
                    No se encontró membresía asociada a este pago en la colección <code className="font-mono text-xs">membresias</code>.
                  </div>
                )}

                {/* datos factura */}
                {hasFact && (
                  <Section title="Datos de factura electrónica">
                    <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2">
                      <DL label="RUT empresa"   value={detailPayment.datosFactura.rutEmpresa} />
                      <DL label="Razón social"  value={detailPayment.datosFactura.razonSocial} />
                      <DL label="Giro"          value={detailPayment.datosFactura.giro} />
                    </dl>
                  </Section>
                )}

                {/* datos transferencia */}
                {hasTrans && detailPayment.cuentaDestino && (
                  <Section title="Cuenta bancaria destino">
                    <dl className="grid grid-cols-[auto_1fr_auto_1fr] gap-x-4 gap-y-2">
                      <DL label="Banco"         value={detailPayment.cuentaDestino.banco} />
                      <DL label="Tipo"          value={detailPayment.cuentaDestino.tipoCuenta} />
                      <DL label="N° cuenta"     value={detailPayment.cuentaDestino.numeroCuenta} />
                      <DL label="RUT empresa"   value={detailPayment.cuentaDestino.rutEmpresa} />
                      <DL label="Titular"       value={detailPayment.cuentaDestino.nombreTitular} />
                      <DL label="Email comp."   value={detailPayment.cuentaDestino.emailComprobante} />
                    </dl>
                  </Section>
                )}

                {/* datos técnicos Transbank */}
                {esWebpay(detailPayment) && (
                  <div>
                    <button onClick={() => setDetailTechOpen(o => !o)}
                      className="flex items-center gap-1.5 text-gray-500 hover:text-gray-700 text-xs font-bold">
                      <span>{detailTechOpen ? '▲' : '▼'}</span> Datos técnicos Transbank
                    </button>
                    {detailTechOpen && (
                      <div className="mt-2 rounded-xl border border-gray-200 p-4">
                        <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2">
                          <DL label="buyOrder"       value={detailPayment.buyOrder || '—'} mono />
                          <DL label="sessionId"      value={detailPayment.sessionId || '—'} mono />
                          <DL label="tbkToken"       value={detailPayment.tbkToken || '—'} mono />
                          <DL label="authCode"       value={detailPayment.authorizationCode || '—'} mono />
                          <DL label="responseCode"   value={detailPayment.responseCode ?? '—'} />
                        </dl>
                      </div>
                    )}
                  </div>
                )}

              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
